import { useAppDispatch } from '../grid/reduxStore/Store';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCurrentUser } from '../../../providers/UserProvider';
import type { DefaultSocketResponseType } from '../../../services/socket/SocketEvents';
import { BlockEvents, AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { UndoRedoManager } from '../UndoRedo/UndoRedoManager';
import { UndoRedoCommandForBlocks } from '../UndoRedo/UndoRedoCommand';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice';
import {
  updateBlockSettings,
  setBlockBackgroundColor,
  setBlockBorderColor,
  setBorderRadius,
  setBorderWidth,
  setPaddingValues,
  setAspectRatio,
} from '../grid/reduxStore/blockStyleSettingsSlice';
import { rootStore } from '../grid/reduxStore/Store';
import { normalizeRotation } from '../GridDndEditor/helpers/rotation';

function getExistingBlockSettings(blockId: string): BlockSettings | undefined {
  const state = rootStore.getState();
  return state.blockStyleSettings.blockStyle[blockId];
}

export type BlockDesignHandlerType = (
  blockId: string,
  beforeState: BlockSettings | null,
  afterState: BlockSettings
) => Promise<DefaultSocketResponseType | undefined>;

function updateUIState(dispatch, blockId: string, properties: BlockSettings) {
  if (properties.backgroundColor) {
    dispatch(setBlockBackgroundColor({ blockId, backgroundColor: properties.backgroundColor }));
  }

  if (properties.borderColor) {
    dispatch(setBlockBorderColor({ blockId, borderColor: properties.borderColor }));
  }

  if (properties.borderRadius) {
    dispatch(setBorderRadius({ blockId, borderRadius: properties.borderRadius }));
  }

  if ('borderLeft' in properties || 'borderRight' in properties || 'borderTop' in properties || 'borderBottom' in properties) {
    const updatedStyle = {
      borderLeft: properties.borderLeft || 0,
      borderRight: properties.borderRight || 0,
      borderTop: properties.borderTop || 0,
      borderBottom: properties.borderBottom || 0,
      borderWidth: properties.borderWidth || 0,
    };
    dispatch(setBorderWidth({ blockId, updatedStyle }));
  }

  if ('paddingLeft' in properties || 'paddingRight' in properties || 'paddingTop' in properties || 'paddingBottom' in properties) {
    const currentBlockSettings = getExistingBlockSettings(blockId);

    const paddingStyle = {
      paddingLeft: currentBlockSettings?.paddingLeft ?? 0,
      paddingRight: currentBlockSettings?.paddingRight ?? 0,
      paddingTop: currentBlockSettings?.paddingTop ?? 0,
      paddingBottom: currentBlockSettings?.paddingBottom ?? 0,
      ...(properties.paddingLeft !== undefined ? { paddingLeft: properties.paddingLeft } : {}),
      ...(properties.paddingRight !== undefined ? { paddingRight: properties.paddingRight } : {}),
      ...(properties.paddingTop !== undefined ? { paddingTop: properties.paddingTop } : {}),
      ...(properties.paddingBottom !== undefined ? { paddingBottom: properties.paddingBottom } : {}),
    };

    dispatch(setPaddingValues({ blockId, paddingStyle }));
  }

  if ('imageAspectRatioLock' in properties && typeof properties.imageAspectRatioLock === 'boolean') {
    dispatch(setAspectRatio({ blockId, imageAspectRatioLock: properties.imageAspectRatioLock }));
  }

  dispatch(updateBlockSettings({ blockId, blockSettings: properties as BlockSettings }));
}

export function useBlockDesignHandlerWithoutUndoRedo(): BlockDesignHandlerType {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const { data } = useCurrentUser();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return async (blockId, beforeState, afterState) => {
    if (!blockId) return undefined;

    const currentBlockSettings = getExistingBlockSettings(blockId);

    if (afterState.borderColor !== undefined) {
      dispatch(setBlockBorderColor({ blockId, borderColor: afterState.borderColor }));
    }

    if (afterState.backgroundColor !== undefined) {
      dispatch(setBlockBackgroundColor({ blockId, backgroundColor: afterState.backgroundColor }));
    }

    if (afterState.borderRadius !== undefined) {
      dispatch(setBorderRadius({ blockId, borderRadius: afterState.borderRadius }));
    }

    if (
      afterState.borderLeft !== undefined ||
      afterState.borderRight !== undefined ||
      afterState.borderTop !== undefined ||
      afterState.borderBottom !== undefined ||
      afterState.borderWidth !== undefined
    ) {
      const updatedStyle = {
        borderLeft: afterState.borderLeft ?? beforeState?.borderLeft ?? 0,
        borderRight: afterState.borderRight ?? beforeState?.borderRight ?? 0,
        borderTop: afterState.borderTop ?? beforeState?.borderTop ?? 0,
        borderBottom: afterState.borderBottom ?? beforeState?.borderBottom ?? 0,
        borderWidth: afterState.borderWidth ?? beforeState?.borderWidth ?? 0,
      };
      dispatch(setBorderWidth({ blockId, updatedStyle }));
    }

    const paddingUpdated =
      afterState.paddingLeft !== undefined ||
      afterState.paddingRight !== undefined ||
      afterState.paddingTop !== undefined ||
      afterState.paddingBottom !== undefined;

    let paddingStyle: {
      paddingLeft?: number;
      paddingRight?: number;
      paddingTop?: number;
      paddingBottom?: number;
    } = {};

    if (paddingUpdated) {
      paddingStyle = {
        paddingLeft: currentBlockSettings?.paddingLeft ?? 0,
        paddingRight: currentBlockSettings?.paddingRight ?? 0,
        paddingTop: currentBlockSettings?.paddingTop ?? 0,
        paddingBottom: currentBlockSettings?.paddingBottom ?? 0,
        ...(afterState.paddingLeft !== undefined ? { paddingLeft: afterState.paddingLeft } : {}),
        ...(afterState.paddingRight !== undefined ? { paddingRight: afterState.paddingRight } : {}),
        ...(afterState.paddingTop !== undefined ? { paddingTop: afterState.paddingTop } : {}),
        ...(afterState.paddingBottom !== undefined ? { paddingBottom: afterState.paddingBottom } : {}),
      };

      dispatch(setPaddingValues({ blockId, paddingStyle }));
    }

    if (afterState.imageAspectRatioLock !== undefined) {
      dispatch(setAspectRatio({ blockId, imageAspectRatioLock: afterState.imageAspectRatioLock }));
    }

    updateUIState(dispatch, blockId, afterState);

    dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

    return new Promise<DefaultSocketResponseType>((resolve) => {
      const designPropsToSave = {
        blockId,
        updatedByUserId: Number(data.id),
        lockedBy: afterState.lockedBy ?? null,
        currentTimestamp: new Date().toISOString(),
        borderColor: afterState.borderColor,
        backgroundColor: afterState.backgroundColor,
        borderRadius: afterState.borderRadius,
        borderLeft: afterState.borderLeft,
        borderRight: afterState.borderRight,
        borderTop: afterState.borderTop,
        borderBottom: afterState.borderBottom,
        opacity: afterState.opacity,
        imageAlt: afterState.imageAlt,
        imageLink: afterState.imageLink,
        imageAspectRatioLock: afterState.imageAspectRatioLock,
        top: afterState.top,
        left: afterState.left,
        rotation: afterState.rotation !== undefined ? normalizeRotation(afterState.rotation) : undefined,
        /*shapeType: afterState.shapeType,*/
      };

      const existingPadding = {
        paddingTop: currentBlockSettings?.paddingTop ?? 0,
        paddingBottom: currentBlockSettings?.paddingBottom ?? 0,
        paddingLeft: currentBlockSettings?.paddingLeft ?? 0,
        paddingRight: currentBlockSettings?.paddingRight ?? 0,
      };

      if (paddingUpdated) {
        Object.assign(existingPadding, {
          paddingTop: paddingStyle.paddingTop !== undefined ? paddingStyle.paddingTop : existingPadding.paddingTop,
          paddingBottom: paddingStyle.paddingBottom !== undefined ? paddingStyle.paddingBottom : existingPadding.paddingBottom,
          paddingLeft: paddingStyle.paddingLeft !== undefined ? paddingStyle.paddingLeft : existingPadding.paddingLeft,
          paddingRight: paddingStyle.paddingRight !== undefined ? paddingStyle.paddingRight : existingPadding.paddingRight,
        });
      }

      Object.assign(designPropsToSave, existingPadding);

      Object.keys(designPropsToSave).forEach((key) => {
        if (designPropsToSave[key] === undefined) {
          delete designPropsToSave[key];
        }
      });

      socketClient.publish(BlockEvents.BLOCK_SETTINGS_SAVE, designPropsToSave, (response: DefaultSocketResponseType) => {
        dispatch(setSaveStatusBasedOnApiResponse(response));
        checkDocumentLockStatus(response.errorCode);
        resolve(response);
      });
    });
  };
}

function useUndoRedoBlockDesignHandler(blockDesignHandlerWithoutUndoRedo: BlockDesignHandlerType) {
  return (blockId: string, beforeState: BlockSettings, afterState: BlockSettings) => {
    const undoRedoManager = UndoRedoManager.getUndoRedoManager();

    const beforeStateCopy = JSON.parse(JSON.stringify(beforeState));
    const afterStateCopy = JSON.parse(JSON.stringify(afterState));

    const undoCallback = async (undoRedoCommand: UndoRedoCommandForBlocks): Promise<boolean> => {
      try {
        const mappedBlockId = undoRedoCommand.getMappedBlockId(blockId);
        await blockDesignHandlerWithoutUndoRedo(mappedBlockId, afterStateCopy, beforeStateCopy);
        return true;
      } catch (error) {
        return false;
      }
    };

    const redoCallback = async (undoRedoCommand: UndoRedoCommandForBlocks): Promise<boolean> => {
      try {
        const mappedBlockId = undoRedoCommand.getMappedBlockId(blockId);
        await blockDesignHandlerWithoutUndoRedo(mappedBlockId, beforeStateCopy, afterStateCopy);
        return true;
      } catch (error) {
        return false;
      }
    };

    undoRedoManager.pushUndoRedoCommands(new UndoRedoCommandForBlocks(blockId, undoCallback, redoCallback));
  };
}

export function useBlockSettingsHandler(): { changeBlockDesign: BlockDesignHandlerType } {
  const blockDesignHandlerWithoutUndoRedo = useBlockDesignHandlerWithoutUndoRedo();
  const undoRedoBlockDesignHandler = useUndoRedoBlockDesignHandler(blockDesignHandlerWithoutUndoRedo);

  const changeBlockDesign: BlockDesignHandlerType = async (blockId, beforeState, afterState) => {
    const hasChanges = JSON.stringify(beforeState) !== JSON.stringify(afterState);
    if (!hasChanges || !blockId) return undefined;

    const response = await blockDesignHandlerWithoutUndoRedo(blockId, beforeState, afterState);

    if (response && response.status === AcknowledgmentResponseStatus.OK && beforeState) {
      undoRedoBlockDesignHandler(blockId, beforeState, afterState);
    }

    return response;
  };

  return { changeBlockDesign };
}
